import { apiClient, type Paginated } from '@/core/api';
import type {
  CheckoutSessionInput,
  HostedSessionDto,
  InvoiceDto,
  InvoiceListParams,
  SubscriptionDto,
} from './types';

/**
 * `GET /api/billing/subscription/` — status, trial, seats and price preview.
 *
 * Readable by EVERY member of the school (IsSchoolMember, backend ADR-024) and
 * exempt from the subscription lock, which is what lets both the billing screen
 * and the paused screen keep working while the API is closed to everything else.
 */
export async function getSubscription(): Promise<SubscriptionDto> {
  const { data } = await apiClient.get<SubscriptionDto>('/billing/subscription/');
  return data;
}

/**
 * `POST /api/billing/checkout-session/` — admin only.
 *
 * Returns a Stripe-hosted Checkout URL the browser must be SENT to; there is
 * no embedded form and no card field anywhere in this app (PCI scope stays
 * outside Osvitly). The subscription becomes real when the webhook arrives,
 * never on the browser's redirect back.
 */
export async function createCheckoutSession(
  input: CheckoutSessionInput
): Promise<HostedSessionDto> {
  const { data } = await apiClient.post<HostedSessionDto>('/billing/checkout-session/', input);
  return data;
}

/**
 * `POST /api/billing/portal-session/` — admin only.
 *
 * Cards, plan switches and cancellation all live in Stripe's own portal.
 * Refused with `code: no_billing_customer` before the school has been through
 * Checkout once — surface that message and point at the plan picker.
 */
export async function createPortalSession(): Promise<HostedSessionDto> {
  const { data } = await apiClient.post<HostedSessionDto>('/billing/portal-session/');
  return data;
}

/** `GET /api/billing/invoices/` — the mirrored Stripe invoices, admin only. */
export async function getInvoices(params: InvoiceListParams = {}): Promise<Paginated<InvoiceDto>> {
  const { data } = await apiClient.get<Paginated<InvoiceDto>>('/billing/invoices/', { params });
  return data;
}
